import QRCode from "qrcode";

// Customer-facing links use the live domain (PUBLIC_URL) when set, so QR codes/emails match even when printed from preview
let _origin = window.location.origin;
export const setPublicOrigin = (u) => { if (u) _origin = u.replace(/\/$/, ""); };
export const publicOrigin = () => _origin;

// Build printable code cards (each with a QR that opens the map ready to unlock)
export async function printCodeCards(routeTitle, codes) {
  const origin = publicOrigin();
  const cards = await Promise.all(
    codes.map(async (code) => {
      const url = `${origin}/self-guided?code=${encodeURIComponent(code)}`;
      const qr = await QRCode.toDataURL(url, { width: 320, margin: 1, color: { dark: "#0A0A0A", light: "#FFFFFF" } });
      return { code, qr };
    })
  );

  const cardHtml = cards
    .map(
      ({ code, qr }) => `
      <div class="card">
        <div class="brand">WHANGAMATĀ CYCLES</div>
        <div class="kicker">Self-Guided Map</div>
        <div class="title">${routeTitle}</div>
        <img class="qr" src="${qr}" alt="QR code" />
        <div class="code-label">Your access code</div>
        <div class="code">${code}</div>
        <div class="note">Scan the QR or enter the code at<br/>${origin.replace(/^https?:\/\//, "")}/self-guided<br/><strong>Valid 12 hours from first use.</strong></div>
      </div>`
    )
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${routeTitle} — Access Cards</title>
  <style>
    @page { margin: 12mm; }
    * { box-sizing: border-box; }
    body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; margin: 0; background: #fff; }
    .grid { display: flex; flex-wrap: wrap; gap: 10mm; padding: 6mm; }
    .card { width: 85mm; border: 1.5px solid #0A0A0A; border-radius: 6mm; padding: 8mm 6mm; text-align: center; page-break-inside: avoid; color: #0A0A0A; }
    .brand { font-size: 9px; letter-spacing: 3px; color: #001A4D; font-weight: 700; }
    .kicker { font-size: 10px; letter-spacing: 2px; color: #666; margin-top: 2mm; text-transform: uppercase; }
    .title { font-size: 22px; font-weight: 700; margin: 3mm 0 5mm; }
    .qr { width: 45mm; height: 45mm; }
    .code-label { font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #666; margin-top: 4mm; }
    .code { font-family: monospace; font-size: 30px; font-weight: 700; letter-spacing: 6px; color: #001A4D; margin-top: 1mm; }
    .note { font-size: 10px; color: #444; margin-top: 5mm; line-height: 1.5; }
  </style></head>
  <body><div class="grid">${cardHtml}</div></body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 500);
  return true;
}

// Front-desk poster: one QR that opens the Echo booking page
export function printDaySheet(dateLabel, bookings) {
  const w = window.open("", "_blank");
  if (!w) return false;
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const rows = bookings.map((b) => {
    const masters = (b.codes || []).filter((c) => c.role !== "extra");
    const secs = (b.codes || []).filter((c) => c.role === "extra");
    return `<tr>
      <td><strong>${esc(b.start_time || "—")}</strong><br><span class="muted">${b.hours}h</span></td>
      <td><strong>${esc(b.customer_name)}</strong><br><span class="muted">${esc(b.ref)} · ${esc(b.customer_phone || "")}</span></td>
      <td>${b.bikes.map((i) => `${i.qty}× ${esc(i.bike_name)}`).join("<br>") || "<span class='muted'>own bike</span>"}</td>
      <td>${b.tours.map((t) => `${esc(t.title)}${t.extra_riders ? ` <span class="muted">+${t.extra_riders} secondary</span>` : ""}`).join("<br>") || "<span class='muted'>—</span>"}</td>
      <td class="mono">${masters.map((c) => c.code).join("<br>") || "—"}</td>
      <td class="mono">${secs.map((c) => `☐ ${c.code}`).join("<br>") || "—"}</td>
      <td class="${b.payment.status === "paid" ? "paid" : "unpaid"}">${b.payment.status === "paid" ? "PAID" : `UNPAID<br>$${b.total.toFixed(2)}`}<br><span class="muted">${esc(b.status)}</span></td>
    </tr>`;
  }).join("");
  w.document.write(`<!doctype html><html><head><title>Day sheet ${esc(dateLabel)}</title><style>
    @page{size:A4 landscape;margin:12mm}
    body{font-family:Helvetica,Arial,sans-serif;color:#0A0A0A;margin:0}
    h1{font-family:Georgia,serif;font-weight:300;font-size:26px;margin:0}
    .brand{font-family:monospace;font-size:11px;letter-spacing:.2em;color:#001A4D}
    table{width:100%;border-collapse:collapse;margin-top:14px;font-size:12px}
    th{text-align:left;font-family:monospace;font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#001A4D;border-bottom:2px solid #001A4D;padding:6px 8px}
    td{padding:8px;border-bottom:1px solid #DDD;vertical-align:top}
    .mono{font-family:monospace;font-size:13px;letter-spacing:.08em}
    .muted{color:#777;font-size:11px}
    .paid{color:#1B6B3A;font-weight:700}.unpaid{color:#9A3F00;font-weight:700}
    .foot{margin-top:14px;font-size:11px;color:#555}
  </style></head><body>
    <div class="brand">WHANGAMATĀ CYCLES · ECHO · DAY SHEET</div>
    <h1>${esc(dateLabel)} — ${bookings.length} booking${bookings.length === 1 ? "" : "s"}</h1>
    <table><thead><tr><th>Time</th><th>Rider</th><th>Bikes</th><th>Tours</th><th>Master code (emailed)</th><th>Secondary codes — hand out ☐</th><th>Paid</th></tr></thead><tbody>${rows || `<tr><td colspan="7" class="muted">No bookings.</td></tr>`}</tbody></table>
    <p class="foot">Tick each secondary code as you hand the card over. Mark payments in Admin → Bookings → Mark paid.</p>
    <script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
  return true;
}

export async function printQRPoster({ url, title, sub, filename = "echo-qr" }) {
  const qr = await QRCode.toDataURL(url, { width: 520, margin: 1, color: { dark: "#001A4D", light: "#FFFFFF" } });
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${filename}</title><style>
    body{margin:0;font-family:Georgia,serif;background:#F5F5F0;color:#0A0A0A;display:flex;align-items:center;justify-content:center;min-height:100vh}
    .poster{width:560px;padding:48px;text-align:center;border:2px solid #001A4D;border-radius:24px;background:#fff}
    .brand{font-family:monospace;font-size:13px;letter-spacing:.2em;color:#001A4D}
    h1{font-weight:300;font-size:44px;margin:14px 0 6px;line-height:1}
    p{color:#444;font-size:16px;margin:0 0 24px}
    img{width:360px;height:360px}
    .url{font-family:monospace;font-size:14px;color:#001A4D;margin-top:20px}
    @media print{body{background:#fff}}
  </style></head><body><div class="poster">
    <div class="brand">WHANGAMATĀ CYCLES · ECHO</div>
    <h1>${title}</h1>
    <p>${sub}</p>
    <img src="${qr}" alt="QR" />
    <div class="url">${url.replace(/^https?:\/\//, "")}</div>
  </div><script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
}

export async function downloadQRPng(url, filename) {
  const data = await QRCode.toDataURL(url, { width: 1024, margin: 2, color: { dark: "#001A4D", light: "#FFFFFF" } });
  const a = document.createElement("a");
  a.href = data; a.download = `${filename}.png`; a.click();
}

export function printBookingQR(storeMode = false) {
  const url = `${publicOrigin()}/book${storeMode ? "?mode=store" : ""}`;
  return printQRPoster({
    url, title: storeMode ? "Front desk booking" : "Book your ride here",
    sub: `Scan to hire a bike, add a self-guided adventure,<br/>sign the waiver — then pay cash or EFTPOS at the counter.`,
  });
}
